import type { CellTypeReport, CellTypesConfig } from "./types";
import { analyzeCellTypes } from "./analyzer";
import { loadCellTypesConfig } from "./storage";
import {
  findUncataloguedFunctions,
  type UncataloguedFunction,
} from "./uncatalogued";
import { getWorkbookModel } from "../model";

/**
 * Scan the entire workbook and produce a type report for every non-empty
 * cell that has at least one detected type signal. Also reports any
 * function calls in formulas that aren't in the catalog — useful for
 * spotting gaps before they cause silent inference misses.
 *
 * Reads user-configured weights, direct-rule toggles, and the function
 * catalog from `src/celltypes/storage.ts` before scanning. Defaults are
 * used when storage is empty.
 *
 * Shares the workbook scan with audit / strings / formulas / info /
 * data-islands — if any of them already cached the model, this is
 * essentially free.
 */
export async function scanCellTypes(): Promise<{
  reports: CellTypeReport[];
  config: CellTypesConfig;
  uncatalogued: UncataloguedFunction[];
}> {
  const config = await loadCellTypesConfig();
  const model = await getWorkbookModel("workbook");
  const sheets = Array.from(model.sheets.values());
  const reports = analyzeCellTypes(sheets, model, config);
  const uncatalogued = findUncataloguedFunctions(sheets, model, config.catalog);
  return { reports, config, uncatalogued };
}
